import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Button } from "components/ui/button";
import { Dialog, DialogContent } from "components/ui/dialog";
import { useGetUsersQuery, } from "features/iam/hooks/use-iam";
import { Search, X, Users, Loader2, Mail, User } from 'lucide-react';
import { debounce } from 'lodash';
import { useTheme } from "components/core/theme-provider";
import { useTranslation } from "react-i18next";
import { useInviteUsers } from "../hooks/use-invite";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useChatStore } from "state/store/chat/use-chat-store";
import { useGetConversationById } from "hooks/use-conversation";
import { useCommonStore } from "state/store/common/useCommonStore";

import { useToast } from "hooks/use-toast";
import { useModelStore } from "state/store/chat/use-model-store";
import { createConversationTitleFromUsersArray } from "utils/chat-utilities";
import userParticipant from 'assets/images/userParticipant.png'

// import { useConfig } from "hooks/use-config";

interface InviteUserToGroupModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const InviteUserToGroupModal: React.FC<InviteUserToGroupModalProps> = ({
    isOpen,
    onClose,
}) => {
    const { toast } = useToast();
    // const { configurationSettings } = useConfig();


    const hasParticipantsArray = useChatStore((state) => state.hasParticipantsArray);
    // console.log('hasParticipantsArray', hasParticipantsArray);

    const currentUser = useCommonStore((state) => state.user);
    const selectedModel = useModelStore((state) => state.selectedModel);


    const conversationId = useParams()?.conversationId;
    const [searchParams] = useSearchParams();
    const isIncognitoChat = searchParams.get('incognito-chat') === 'true';
    // const hasParticipants = useChatStore((state) => state.hasParticipant); 
    const { data: conversation } = useGetConversationById(conversationId as string, isIncognitoChat);

    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

    // console.log('hasParticipantsArray', hasParticipantsArray, "selectedUsers", selectedUsers);
    const [searchQuery, setSearchQuery] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [searchMode, setSearchMode] = useState<'email' | 'name'>('name');
    const navigate = useNavigate();

    const { theme } = useTheme();
    const { t } = useTranslation();
    const inviteUsersMutation = useInviteUsers();

    const debouncedChange = useMemo(
        () =>
            debounce((val: string) => {
                setSearchQuery(val);
            }, 500),
        []
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        debouncedChange(value);
    };

    const queryFilter = useMemo(() => {
        if (searchQuery) {
            return searchMode === 'email'
                ? { email: searchQuery }
                : { name: searchQuery };
        }
        return null;
    }, [searchQuery, searchMode]);

    const toggleSearchMode = () => {
        setSearchMode(prev => prev === 'email' ? 'name' : 'email');
    };

    const {
        data,
        isLoading: isUsersLoading,
    } = useGetUsersQuery({
        page: 0,
        pageSize: 50,
        filter: queryFilter || {},
        enabled: isOpen && !!queryFilter,
    });

    const allUsers = useMemo(() => {
        const users = data?.data || [];
        if (!currentUser?.id) return users;
        return users.filter((user: any) => user.itemId !== currentUser.id && !hasParticipantsArray.some((participant: any) => participant.itemId === user.itemId));
    }, [data?.data, currentUser?.id]);

    const handleUserToggle = useCallback((user: any) => {
        setSelectedUsers(prev => {
            const isSelected = prev.some(u => u.itemId === user.itemId);
            if (isSelected) {
                return prev.filter(u => u.itemId !== user.itemId);
            } else {
                return [...prev, user];
            }
        });
    }, []);

    const handleRemoveUser = useCallback((userId: string) => {
        setSelectedUsers(prev => prev.filter(u => u.itemId !== userId));
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setSelectedUsers([]);
            setSearchQuery('');
            setInputValue('');
            setSearchMode('name');
        }
    }, [isOpen]);


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (selectedUsers.length === 0) return;
        const alreadyAddedUsers = hasParticipantsArray.map(user => user.itemId);
        const newUsers = selectedUsers.filter(user => !alreadyAddedUsers.includes(user.itemId));
        if (newUsers.length === 0) {
            toast({
                variant: 'destructive',
                title: t('ERROR'),
                description: t('NO_NEW_USERS_TO_ADD'),
            });
            return;
        }

        let conversationName = '';
        if (conversation && conversation?.events?.length > 0 && conversation?.events.find((event: any) => event.eventType === 'CONVERSATION_TITLE_UPDATED')) {
            conversationName = conversation.conversationTitle;
        } else {
            let users = [];
            if (hasParticipantsArray.length > 0) {
                users = [...newUsers, ...hasParticipantsArray,];

            } else {
                users = [...newUsers, currentUser];

            }
            conversationName = createConversationTitleFromUsersArray(users);

        }

        try {

            const payload = {
                invitationType: 'ADD_USER' as const,
                userIds: newUsers.map(user => user.itemId),
                conversationId: conversationId || '',
                assistantId: selectedModel?.id,
                conversationTitle: conversationName,
            };


            const response = await inviteUsersMutation.mutateAsync(payload);
            // console.log('Invitation response:', response);

            if (response?.conversationId) {
                navigate(`/chat/${response?.conversationId}`);
                const newSidebarEntry = {
                    ConversationId: response.conversationId,
                    ConversationTitle: conversationName,
                    CreatedDate: new Date().toISOString(),
                    LastUpdatedDate: new Date().toISOString(),
                    Conversations: [],
                    IsGroup: true,
                    TenantId: '',
                    CreatedBy: currentUser?.id ?? '',
                };
                useChatStore.getState().appendToSidebarConversationsList(newSidebarEntry);
            }
            onClose();
            toast({
                variant: 'success',
                title: t('SUCCESS'),
                description: t('INVITE_USERS_SUCCESS'),
            });
        } catch (error) {
            console.error('Failed to send invitation:', error);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent hideClose className="bg-white rounded-xl w-[90vw] sm:w-[464px] min-w-[350px] min-h-[476px] max-h-[60vh] shadow-lg flex flex-col border-none">

                <button
                    onClick={() => {
                        console.log('clicked');
                        setSelectedUsers([]);
                        setSearchQuery('');
                        setInputValue('');
                        onClose();
                    }}
                    className="absolute text-xs cursor-pointer p-1 border bg-white border-[#959595] w-5 h-5 flex items-center justify-center rounded-full  right-4 top-4 text-[#959595] hover:text-[#959595]"
                >
                    ✕
                </button>

                <div>
                    <h2 className="text-2xl font-semibold ">{t('ADD_MEMBERS') || "Add Participants"}</h2>
                    <p className="text-sm mb-4 font-light">
                        {t('ADD_MEMBERS_TO_GROUP_DESCRIPTION') || "Search and invite participants to join the group chat"}
                    </p>
                </div>


                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                    <div className="mb-4">
                        {/* <label className="block text-sm font-medium mb-2">{t('SEARCH_PARTICIPANTS') || "Search Participants"}</label> */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-primary" />
                            <input
                                type="text"
                                placeholder={`${t("SEARCH_BY")} ${searchMode === 'email' ? t('EMAIL').toLowerCase() : t('NAME').toLowerCase()}...`}
                                value={inputValue}
                                onChange={handleInputChange}
                                autoComplete="off"
                                className="
                                        h-10 w-full rounded-3xl
                                        border border-gray-300
                                        bg-background pl-9 pr-8
                                        focus:outline-none
                                        focus:border-primary
                                        focus:ring-1 focus:ring-primary
                                    "
                            />
                            <Button
                                variant="ghost"
                                size="sm"
                                type="button"
                                onClick={toggleSearchMode}
                                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 px-2 text-primary"
                                title={searchMode === 'email' ? t('SWITCH_TO_NAME_SEARCH') || 'Switch to name search' : t('SWITCH_TO_EMAIL_SEARCH') || 'Switch to email search'}
                            >
                                {searchMode === 'email' ? <Mail className="h-4 w-4" /> : <User className="h-4 w-4" />}
                            </Button>
                        </div>
                    </div>

                    {selectedUsers.length > 0 && (
                        <div className="mb-4">
                            <div className="flex flex-wrap gap-2">
                                {selectedUsers.map(user => (
                                    <div
                                        key={user.itemId}
                                        className="flex items-center gap-1 bg-primary text-gray-50 px-2 py-1 rounded text-xs"
                                    >
                                        <span>{user.firstName} {user.lastName}</span>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveUser(user.itemId)}
                                            className="hover:text-blue-600"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}


                    <div className="mb-4 flex-1 min-h-0 overflow-y-auto">
                        {searchQuery ? (
                            <div className="space-y-2">
                                {allUsers.map((userItem) => {
                                    const isSelected = selectedUsers.some(u => u.itemId === userItem.itemId);
                                    return (
                                        <button
                                            key={userItem.itemId}
                                            type="button"
                                            onClick={() => handleUserToggle(userItem)}
                                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${theme === 'dark' ? 'hover:bg-gray-900' : 'hover:bg-gray-200'} w-full text-left ${isSelected ? ' border border-primary-200' : ''
                                                }`}
                                        >
                                            <div className="flex-1">
                                                <div className={`text-sm font-medium ${theme === 'dark' ? 'text-primary' : 'text-gray-900'}`}>
                                                    {userItem.firstName} {userItem.lastName}
                                                </div>
                                                <div className="text-xs text-gray-500">{userItem.email}</div>
                                            </div>
                                            {isSelected && <Users className="w-4 h-4 text-primary" />}
                                        </button>
                                    );
                                })}

                                {isUsersLoading && (
                                    <div className="text-center py-2 text-sm text-gray-500">
                                        {t('LOADING_USERS') || "Loading users..."}...
                                    </div>
                                )}

                                {allUsers?.length === 0 && !isUsersLoading && (
                                    <div className="text-center py-4 text-sm text-gray-500">
                                        {t('NO_USERS_FOUND') || "No users found..."}
                                    </div>
                                )}
                            </div>
                        ) : hasParticipantsArray.length > 1 ? (
                            <div className="space-y-2">
                                <div className="flex items-center flex-wrap gap-2 w-full">
                                    {hasParticipantsArray.map((user) => (

                                        <div key={user.itemId} className="min-w-[150px] max-h-8 h-full flex items-center p-2 gap-2 bg-primary-400 rounded-2xl">
                                            {user?.profileImageUrl ? (
                                                <img src={user?.profileImageUrl || ''} alt={user.firstName} className="w-3 h-3 rounded-full" />
                                            ) : (
                                                <div className="w-4 h-4 rounded-full bg-muted flex items-center justify-center border border-border">
                                                    {theme === 'dark' ? (
                                                        <img src={userParticipant} alt="user participant" className="w-3 h-3" style={{ filter: 'invert(1) brightness(1.2)' }} />
                                                    ) : (
                                                        <img src={userParticipant} alt="user participant" className="w-3 h-3" style={{ filter: 'none' }} />
                                                    )}
                                                </div>
                                            )}
                                            <div className="text-sm font-normal leading-[20px] text-white">{user.firstName} {user.lastName}</div>
                                            <button
                                                onClick={() => handleRemoveUser(user.itemId)}
                                                className=" cursor-pointer p-2 border border-white w-3 h-3 flex items-center text-xs justify-center rounded-full text-white hover:text-white"
                                            >
                                                ✕
                                            </button>

                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (<>

                        </>)}


                    </div>

                    <div className={`flex justify-end mt-auto pt-4 border-t-[0.5px] ${theme === 'dark' ? 'border-[#AC9578]' : 'border-[#E9E9EA]'}`}>
                        <Button
                            type="submit"
                            disabled={selectedUsers.length === 0 || inviteUsersMutation.isPending}
                        >
                            {inviteUsersMutation.isPending ? (
                                <>
                                    {t('INVITE') || "Invite"}
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />

                                </>
                            ) : (
                                `${t('INVITE') || "Invite"} (${selectedUsers.length})`
                            )}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};